import React, {useMemo} from 'react';
import {Box, Stack, styled} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Icon,
  Tooltip,
} from '@lightricks/react-design-system';
import {AssetErrors} from '@contentlab/stores/contentLabStore';
import {Asset} from '@contentlab/types/asset';
import translate from '@/utils/translate';

interface GenerateProjectProps {
  testID?: string;
  description: string;
  assetErrors?: AssetErrors[];
  readyAssets?: Asset[];
  disabled?: boolean;
  creditsCost?: number;
  isLoading?: boolean;
  onGenerateClick: () => void;
  tooltipText: string;
}

const GenerateProjectContainer = styled(Box)(() => ({
  borderRadius: '32px',
  width: '100%',
  padding: '24px',
  boxSizing: 'border-box',
  border: `1px solid ${designSystemToken('semantic.bg.neutral-secondary')}`,
  background: designSystemToken('semantic.bg.secondary'),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '24px',
}));

const GenerateButtonContainer = styled(Stack)(() => ({
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
}));

function GenerateProject({
  testID = 'generate-project',
  description,
  assetErrors,
  readyAssets,
  disabled = true,
  creditsCost,
  isLoading,
  tooltipText,
  onGenerateClick,
}: GenerateProjectProps) {
  const descriptionText = useMemo(() => {
    const assetErrorsIds = new Set(
      assetErrors?.map((assetError) => assetError.assetId) || []
    );
    if (
      assetErrors?.length &&
      readyAssets?.every((asset) => assetErrorsIds.has(asset.id))
    ) {
      return translate('Fix the file issues to start editing');
    }
    return description;
  }, [description, readyAssets, assetErrors]);

  const infoTooltip = useMemo(() => {
    if (disabled || !creditsCost || isLoading) {
      return descriptionText;
    }

    if (creditsCost) {
      return translate('This will use x credits', {count: creditsCost});
    }

    return tooltipText;
  }, [disabled, creditsCost, isLoading, descriptionText, tooltipText]);

  return (
    <GenerateProjectContainer data-testid={testID}>
      <GenerateButtonContainer
        direction="row"
        width="100%"
        alignItems="center"
        justifyContent="center"
        gap="16px"
      >
        <Button
          disabled={disabled}
          fullWidth
          size="large"
          appearance="neutral"
          mode="filled"
          isLoading={isLoading}
          onClick={onGenerateClick}
          sx={{
            maxWidth: '400px',
          }}
        >
          {translate('Generate')}
        </Button>
        <Tooltip
          title={
            <Body size="md" color={designSystemToken('semantic.fg.primary')}>
              {infoTooltip}
            </Body>
          }
          arrow
          type="light"
          placement="top-start"
          disableHoverListener={!infoTooltip}
          sx={{
            '.MuiTooltip-tooltip': {
              maxWidth: 'none',
            },
          }}
        >
          <span style={{height: '24px'}}>
            <Icon name="Actions-Info-Line" appearance="primary" size="medium" />
          </span>
        </Tooltip>
      </GenerateButtonContainer>
    </GenerateProjectContainer>
  );
}

export default GenerateProject;
